import React, { useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
  TablePagination,
  TableSortLabel,
  Box,
  Chip,
} from "@mui/material";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import { SortType } from "../../../models/SortType";
import { OnelinkRequest } from "../../../models/Order/OnelinkRequest";
import { formatDate } from "../common/functions/formatDate";
import AppButton from "../common/buttons/AppButton";

interface OnelinkRequestsTableProp {
  maxHeight: number;
  onelinkRequests: OnelinkRequest[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  selectedShipmentIdsMap: Map<number, number>;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  selectAllItem: () => void;
  selectItem: (onelinkRequest: OnelinkRequest) => void;
  deSelectAllItem: () => void;
  deSelectItem: (onelinkRequest: OnelinkRequest) => void;
  approveOnelinkRequest: (id: number, order_id: number) => void;
  rejectOnelinkRequest: (id: number, order_id: number) => void;
  canApprove: boolean;
}

const OnelinkRequestsTable = ({
  maxHeight,
  onelinkRequests,
  page_no,
  limit,
  total_pages,
  total_count,
  sortBy,
  sortType,
  selectedShipmentIdsMap,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  selectAllItem,
  selectItem,
  deSelectAllItem,
  deSelectItem,
  canApprove,
  approveOnelinkRequest,
  rejectOnelinkRequest,
}: OnelinkRequestsTableProp) => {
  const [selectedId] = useState<number[]>([]);
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const areAllSelected = (): boolean => {
    return (
      selectedShipmentIdsMap.size > 0 &&
      selectedShipmentIdsMap.size === onelinkRequests.length
    );
  };

  const areSomeSelected = (): boolean => {
    return (
      selectedShipmentIdsMap.size > 0 &&
      selectedShipmentIdsMap.size !== onelinkRequests.length
    );
  };

  return (
    <>
      <Paper sx={{ marginTop: "0.6rem" }}>
        {selectedId?.length > 0 && (
          <Typography sx={{ paddingLeft: 2, paddingTop: 1 }}>
            {`Selected - ` + selectedId?.length}
          </Typography>
        )}

        <TableContainer sx={{ maxHeight: maxHeight }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* <StyledTableCell
                  padding="checkbox"
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 20,
                    textAlign: "center",
                    fontSize: 14,
                  }}
                >
                  <Checkbox
                    color="primary"
                    indeterminate={areSomeSelected()}
                    checked={areAllSelected()}
                    onClick={() => {
                      areAllSelected() ? deSelectAllItem() : selectAllItem();
                    }}
                  />
                </StyledTableCell> */}
                <StyledTableCell
                  sx={{
                    textAlign: "center",
                    fontSize: 14,
                    position: "sticky",
                    left: 0,
                    zIndex: 12,
                    boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                  }}
                >
                  <TableSortLabel
                    active={sortBy === "id"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "id",
                        sortBy === "id" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("id")}</span>}
                    sx={{ fontSize: "12px" }}
                  >
                    ID
                  </TableSortLabel>
                </StyledTableCell>
                <AppTableHeaderCell title="Order Id" fontSize="12px" />
                <AppTableHeaderCell title="Code" fontSize="12px" />
                <AppTableHeaderCell title="Customer" fontSize="12px" />
                <AppTableHeaderCell title="Template" fontSize="12px" />
                <AppTableHeaderCell title="Status" fontSize="12px" />
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "first_name"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "first_name",
                        sortBy === "first_name" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("first_name")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    First Name
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell title="Last Name" fontSize="12px" />
                <AppTableHeaderCell title="Email" fontSize="12px" />
                <AppTableHeaderCell title="Expiry Date" fontSize="12px" />
                <AppTableHeaderCell title="Show Products" fontSize="12px" />
                <AppTableHeaderCell title="Multiple Choice" fontSize="12px" />
                <AppTableHeaderCell title="Email Only" fontSize="12px" />
                <AppTableHeaderCell title="Gender Selection" fontSize="12px" />
                <AppTableHeaderCell title="Image Upload" fontSize="12px" />
                <AppTableHeaderCell
                  title="Show Feedback Form"
                  fontSize="12px"
                />
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "created_date"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "created_date",
                        sortBy === "created_date" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("created_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Created Date
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell>
                  <TableSortLabel
                    active={sortBy === "modified_date"}
                    direction={sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      onSortChange(
                        "modified_date",
                        sortBy === "modified_date" && sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("modified_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    Modified Date
                  </TableSortLabel>
                </AppTableHeaderCell>
                {canApprove && (
                  <AppTableHeaderCell
                    title="Actions"
                    fontSize="12px"
                    sx={{
                      position: "sticky",
                      zIndex: 10,
                      minWidth: "80px",
                      right: 0,
                    }}
                  />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {onelinkRequests.map((onelinkRequest: OnelinkRequest) => (
                <StyledTableRow key={onelinkRequest.id}>
                  {/* <TableCell
                    padding="checkbox"
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 12,
                      backgroundColor: `${theme.palette.background.paper}`,
                      textAlign: "center",
                      fontSize: 12,
                    }}
                  >
                    <Checkbox
                      color="primary"
                      checked={
                        selectedShipmentIdsMap.get(onelinkRequest.id) !==
                        undefined
                      }
                      onClick={() => {
                        if (selectedShipmentIdsMap.get(onelinkRequest.id)) {
                          deSelectItem(onelinkRequest);
                        } else {
                          selectItem(onelinkRequest);
                        }
                      }}
                    />
                  </TableCell> */}
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: 12,
                      position: "sticky",
                      left: 0,
                      zIndex: 10,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor: `${theme.palette.background.paper}`,
                    }}
                  >
                    {onelinkRequest.id}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.order_id || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.code || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.customer_name || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.template_name || "-"}
                  </TableCell>

                  <TableCell
                    sx={{
                      padding: "8px",
                      textAlign: "center",
                      fontSize: "12px",
                    }}
                  >
                    <Chip
                      label={
                        onelinkRequest.approved
                          ? "Approved"
                          : onelinkRequest.rejected
                          ? "Rejected"
                          : "Pending"
                      }
                      sx={{
                        backgroundColor: onelinkRequest.approved
                          ? theme.palette.success.light
                          : onelinkRequest.rejected
                          ? theme.palette.error.light
                          : theme.palette.warning.light,
                        color: theme.palette.customColors.white,
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "25px",
                        minWidth: "100px",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.user_first_name || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.user_last_name || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.user_email || "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {onelinkRequest.expiry_date
                      ? formatDate(onelinkRequest.expiry_date)
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {Boolean(onelinkRequest.show_products) ? "True" : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {Boolean(onelinkRequest.has_multiple_choice)
                      ? "True"
                      : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {Boolean(onelinkRequest.email_only) ? "True" : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {Boolean(onelinkRequest.show_gender_selection)
                      ? "True"
                      : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {Boolean(onelinkRequest.show_image_upload)
                      ? "True"
                      : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {Boolean(onelinkRequest.show_feedback_screen)
                      ? "True"
                      : "False"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {formatDate(onelinkRequest.created_date)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: 12 }}>
                    {formatDate(onelinkRequest.modified_date)}
                  </TableCell>
                  {canApprove && (
                    <TableCell
                      sx={{
                        width: "10%",
                        fontSize: "12px",
                        color: theme.palette.customColors.tableGrayHeadingColor,
                        position: "sticky",
                        zIndex: 10,
                        minWidth: "80px",
                        right: 0,
                        background: theme.palette.customColors.white,
                      }}
                    >
                      {Boolean(onelinkRequest.approved) === false &&
                        Boolean(onelinkRequest.rejected) === false && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <AppButton
                              variant="primary"
                              label="Approve"
                              onClick={() => {
                                approveOnelinkRequest(
                                  onelinkRequest.id,
                                  onelinkRequest.order_id
                                );
                              }}
                              customStyles={{ width: "80px" }}
                            />
                            <AppButton
                              variant="secondary"
                              label="Reject"
                              onClick={() => {
                                rejectOnelinkRequest(
                                  onelinkRequest.id,
                                  onelinkRequest.order_id
                                );
                              }}
                              customStyles={{ width: "80px" }}
                            />
                          </Box>
                        )}
                    </TableCell>
                  )}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[30, 50, 100, 150, 200]}
          component="div"
          count={total_count}
          rowsPerPage={limit}
          page={page_no - 1}
          onPageChange={(event, page_no) => onPageChange(event, page_no + 1)}
          onRowsPerPageChange={(event) =>
            handleRowsChange(parseInt(event.target.value, 10))
          }
        />
      </Paper>
    </>
  );
};

export default OnelinkRequestsTable;
